/**
 * Firebase Cloud Messaging — configuration store (scaffolding).
 *
 * Holds the firebaseConfig JSON the admin pastes in the dashboard.
 * Two storage tracks:
 *   1) localStorage `rawan-fcm-config`  — used by the running site immediately.
 *   2) public/firebase-config.json      — pushed to GitHub so every visitor
 *      (and the service worker) can fetch it after deploy.
 *
 * The actual `getMessaging().getToken({ vapidKey })` wiring is intentionally
 * left as a thin helper to be filled when Firebase keys are provided.
 */
import { useEffect, useState } from "react";

const KEY = "rawan-fcm-config";

export type FcmConfig = {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId?: string;
  vapidKey?: string;  // Web Push VAPID key (Firebase Console → Cloud Messaging → Web push certificates)
};

export const EMPTY_FCM: FcmConfig = {
  apiKey: "", authDomain: "", projectId: "", storageBucket: "",
  messagingSenderId: "", appId: "", measurementId: "", vapidKey: "",
};

export function getFcmConfig(): FcmConfig | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    return { ...EMPTY_FCM, ...JSON.parse(raw) } as FcmConfig;
  } catch {
    return null;
  }
}

export function saveFcmConfig(cfg: FcmConfig) {
  localStorage.setItem(KEY, JSON.stringify(cfg));
  window.dispatchEvent(new CustomEvent("rawan:fcm-config-changed"));
}

export function clearFcmConfig() {
  localStorage.removeItem(KEY);
  window.dispatchEvent(new CustomEvent("rawan:fcm-config-changed"));
}

export function useFcmConfig(): FcmConfig | null {
  const [c, setC] = useState<FcmConfig | null>(() => getFcmConfig());
  useEffect(() => {
    const sync = () => setC(getFcmConfig());
    window.addEventListener("rawan:fcm-config-changed", sync);
    return () => window.removeEventListener("rawan:fcm-config-changed", sync);
  }, []);
  return c;
}

/** Lenient parser — accepts the standard Firebase Console snippet or pure JSON. */
export function parseFcmJson(input: string): FcmConfig {
  const trimmed = input.trim();
  // Try direct JSON first.
  try {
    const obj = JSON.parse(trimmed);
    return { ...EMPTY_FCM, ...obj };
  } catch { /* fall through */ }
  // Try to extract { ... } block from a "const firebaseConfig = { ... }" snippet.
  const m = trimmed.match(/\{[\s\S]*\}/);
  if (m) {
    // Quote unquoted keys: convert  apiKey:  →  "apiKey":
    const quoted = m[0]
      .replace(/([{,]\s*)([A-Za-z_][A-Za-z0-9_]*)\s*:/g, '$1"$2":')
      .replace(/'([^']*)'/g, '"$1"');
    try {
      const obj = JSON.parse(quoted);
      return { ...EMPTY_FCM, ...obj };
    } catch { /* ignore */ }
  }
  throw new Error("صيغة JSON غير صالحة — الصق كائن firebaseConfig كاملاً.");
}

/** Builds the JSON that should be written to public/firebase-config.json. */
export function toPublicJson(cfg: FcmConfig): string {
  return JSON.stringify(cfg, null, 2);
}
